import { ComponentProps, PropsWithChildren, ReactNode, SVGProps } from 'react';
import {
  staticNotificationVariants,
  StaticNotificationVariants,
} from './static-notification.variants';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../ui';
import { match } from 'ts-pattern';
import {
  IconExclamationCircle,
  IconExclamationTriangle,
  IconInfoCircle,
} from '@maia/icons';

type Props = {
  className?: string;
} & StaticNotificationVariants;

export function StaticNotification(props: PropsWithChildren<Props>) {
  const { variant, size, className, children } = props;

  const Icon = mapVariantToIcon(variant);

  return (
    <div
      className={twMerge(
        staticNotificationVariants({ variant, size }),
        className,
      )}
    >
      <div className="leading-none">
        <Icon
          className={twMerge(
            mapSizeToIconSize(size),
            mapSizeToIconColor(variant),
          )}
        />
      </div>

      <Typography variant={mapSizeToTypographyVariant(size)} as="div">
        {children}
      </Typography>
    </div>
  );
}

export function mapSizeToTypographyVariant(
  size: StaticNotificationVariants['size'],
): ComponentProps<typeof Typography>['variant'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['variant']>()
    .with('small', () => 'body-xs')
    .with('default', () => 'body-s')
    .otherwise(() => 'body-s');
}

export function mapSizeToIconSize(
  size: StaticNotificationVariants['size'],
): string {
  return match(size)
    .returnType<string>()
    .with('small', () => 'min-w-16 w-16 h-16 md:min-w-20 md:w-20 md:h-20')
    .with('default', () => 'min-w-24 w-24 h-24')
    .otherwise(() => 'min-w-24 w-24 h-24');
}

export function mapVariantToIcon(
  variant: StaticNotificationVariants['variant'],
): (props: SVGProps<SVGSVGElement>) => ReactNode {
  return match(variant)
    .returnType<(props: SVGProps<SVGSVGElement>) => ReactNode>()
    .with('default', () => IconInfoCircle)
    .with('success', () => IconInfoCircle)
    .with('error', () => IconExclamationCircle)
    .with('warning', () => IconExclamationTriangle)
    .with('info', () => IconInfoCircle)
    .otherwise(() => IconInfoCircle);
}

export function mapSizeToIconColor(
  variant: StaticNotificationVariants['variant'],
): string {
  return match(variant)
    .returnType<string>()
    .with('default', () => 'fill-grayscale600')
    .with('success', () => 'fill-success300')
    .with('error', () => 'fill-error300')
    .with('warning', () => 'fill-warning300')
    .with('info', () => 'fill-info300')
    .otherwise(() => 'fill-grayscale600');
}
